import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "../stores/useAuthStore";
import {
  getUserProfile,
  getUserPreferences,
  updateUserPreferences,
} from "../dataAccess/apiService";
import { UserPreferences } from "@clients/velys-infrastructure/api";
import { isPublicRoute } from "../helpers";

export function useUserProfile(surgeonID?: string) {
  const user_profile = useAuthStore((state) => state.user_profile);
  const userId = user_profile?.sub;

  const userProfileQuery = useQuery({
    queryKey: ["get-user-profile", userId, surgeonID],
    queryFn: () => {
      if (!userId) {
        return Promise.reject(new Error("Missing userId"));
      }
      if (surgeonID) {
        return getUserProfile(surgeonID);
      } else {
        return getUserProfile(userId);
      }
    },
    // Never run on public routes (e.g. /legal) — a rehydrated stale token
    // would 401 and force-logout the anonymous visitor.
    enabled: (!!userId || !!surgeonID) && !isPublicRoute(),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  return { userProfileQuery };
}

export function useUserPreferences(skip?: boolean) {
  const user_profile = useAuthStore((state) => state.user_profile);
  const userId = user_profile?.sub;

  const userPreferencesQuery = useQuery({
    queryKey: ["get-user-preferences", userId],
    queryFn: () => {
      if (!userId) {
        return Promise.reject(new Error("Missing userId"));
      }
      return getUserPreferences(userId);
    },
    enabled: !skip && !!userId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });

  return { userPreferencesQuery };
}

export function useUpdatePreferences() {
  const user_profile = useAuthStore((state) => state.user_profile);
  const userId = user_profile?.sub;
  const queryClient = useQueryClient();

  const updatePreferencesMutation = useMutation({
    mutationFn: (newPreferences: UserPreferences) => {
      if (!userId) {
        throw new Error("Missing userId");
      }

      return updateUserPreferences(userId, newPreferences);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get-user-preferences"],
      });
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return updatePreferencesMutation;
}
